import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import eventsAPI from "../api/eventsAPI";
import { useEvent } from "../context/EventContext";
import Loader from "../components/Loader/Loader";
import EventBanner from "../assest/event-page-banner.jpg";
import { useAuth } from "../context/AuthContext";
import CreateEvent from "../components/Home/CreateEvent";
import defaultEventImage from "../assest/event-page/event-1.jpg";

const Categories = [
	"All",
	"Company Retreats",
	"Tradeshows",
	"Product Launch",
	"Conferences",
	"Workshops",
	"Seminars",
	"Trainings",
	"Charity Events",
	"Hackathons",
	"Job Fair",
];

const Locations = [
	"All",
	"Mumbai",
	"Pune",
	"Bangalore",
	"Chennai",
];

// Component to render the event page, which shows a list of events that
// can be filtered by category and location. It also has a button to create
// a new event and a button to register for an event if the user is not the
// organizer of the event.
const Event = () => {

	const [events, setEvents] = useState([]);
	const [activeCategory, setActiveCategory] = useState("All");
	const [activeLocation, setActiveLocation] = useState("All");
	const [loadingImages, setLoadingImages] = useState({});
	const {
		fetchEventDetails,
		formattedDate,
		loadingEvents,
		setEventCreated,
		setLocationAdded,
		setSpeakerAdded,
		setSponsorAdded,
		setStallAdded,
		registeredEvents,
	} = useEvent();
	const navigate = useNavigate();

	useEffect(() => {
		const fetchEvents = async () => {
			try {
				const response = await eventsAPI.get("/data");
				const data = await response.data;
				setEvents(data);
			} catch (error) {
				console.log("Error fetching Events:", error);
			}
		};
		fetchEvents();
	}, []);

	//   console.log(events);

	//   Handles the click event of a category button in the event page.
	//   Updates the activeCategory state to the category name of the button clicked.
	const handleCategoryClick = (category) => {
		setActiveCategory(category);
	};

	//   Handles the change event of the location dropdown in the event page.
	//   Updates the activeLocation state to the selected location name.
	const handleLocationChange = (event) => {
		setActiveLocation(event.target.value);
	};

	//   Handles the click event of the "Register" button in the event page.
	//   Fetches the event details of the selected event and sets the eventCreated state to false.
	//   Also sets the locationAdded, speakerAdded, sponsorAdded, and stallAdded states to false.
	//   Finally, navigates to the event dashboard page.
	const handleRegisterClick = async (eventId) => {
		await fetchEventDetails(eventId, false);
		setEventCreated(false);
		setLocationAdded(false);
		setSpeakerAdded(false);
		setSponsorAdded(false);
		setStallAdded(false);
		navigate("/eventdashboard");
	};

	//   Handles the click event of the "Organize" button in the event page.
	//   Fetches the event details of the selected event and sets the eventCreated state to false.
	//   Checks if the user is logged in and navigates to the login page if not.
	//   Otherwise, navigates to the organizer dashboard page.
	const handleOrganizeClick = async (eventId) => {
		try {
			// Fetch event details from the database
			await fetchEventDetails(eventId, true);

			// Check if the user is logged in
			if (!user) {
				navigate("/login");
			} else {
				// Navigate to the organizer dashboard after fetching the event details
				navigate("/organizerdashboard");
			}
		} catch (error) {
			console.log("Error fetching event details:", error);
		}
	};

	// loading image function
	const handleImageLoad = (eventId) => {
		setLoadingImages((prevLoadingImages) => ({
			...prevLoadingImages,
			[eventId]: false,
		}));
	};

	// handle error of loading image
	const handleImageError = (eventId) => {
		setLoadingImages((prevLoadingImages) => ({
			...prevLoadingImages,
			[eventId]: false,
		}));
	};

	const filteredEvents = events.filter((event) => {
		const categoryMatch =
			activeCategory === "All" ||
			event.event_type.toLowerCase() === activeCategory.toLowerCase();
		const locationMatch =
			activeLocation === "All" || event.location === activeLocation;
		return categoryMatch && locationMatch;
	});

	const { isLoggedIn } = useAuth();
	const { setIsModalOpen } = useEvent();

	const handleCreateEvent = () => {
		if (isLoggedIn) {
			setIsModalOpen(true);
		} else {
			navigate("/login");
		}
	};

	const { user } = useAuth();

	const isUserRegistered = (eventId) => {
		return registeredEvents.some(
			(registeredEvent) => registeredEvent._id === eventId
		);
	};


	return (
		<div className="Event-section">
			<CreateEvent />
			<div className="image-wrapper">
				<img src={EventBanner} alt="banner" />
			</div>
			<div className="Event-content">
				<div className="event-filters">
					<p>Category</p>
					{Categories.map((category) => (
						<button
							key={category}
							onClick={() => handleCategoryClick(category)}
							className={activeCategory === category ? "active" : ""}
						>
							{category}
						</button>
					))}
					<p>Location</p>
					<select
						className="select-location"
						onChange={handleLocationChange}
						value={activeLocation}
					>
						{Locations.map((location) => (
							<option key={location} value={location}>
								{location}
							</option>
						))}
					</select>
				</div>
				<div className="right-side-Events">
					<button className="create-event" onClick={() => handleCreateEvent()}>
						Create Event
					</button>
					<div className="event-list-items">
						{filteredEvents.length > 0
							? filteredEvents.map((event, index) => {

								const isOrganizer = user?.id && user?.id === event.organizerID;
								const isRegistered = isUserRegistered(event._id);

								return (
									<div className="event-card" key={index}>
										<figure>
											{loadingImages[event._id]
												? <Loader />
												: (
													<img
														src={
															event.image_url
																? event.image_url
																: defaultEventImage
														}
														alt="event card"
														onLoad={() => handleImageLoad(event._id)}
														onError={() => handleImageError(event._id)}
													/>
												)}
											<figcaption className="caption">
												{event.event_type}
											</figcaption>
										</figure>
										<div className="card-data">
											<div className="card-data-flex">
												<h3>{event.event_name}</h3>
												<p className="card-data-date">
													🗓 {formattedDate(event.eventDuration[0].start)} |{" "}
													{formattedDate(event.eventDuration[event.eventDuration.length - 1].end)}{" "}
												</p>
												<p className="card-data-location">
													📍 {event.location}
												</p>
												{isOrganizer
													? (
														<button
															className="read-more"
															onClick={() => handleOrganizeClick(event._id)}
														>
															Organize
														</button>
													) : (
														<button
															className="read-more"
															onClick={() => handleRegisterClick(event._id)}
														>
															{isRegistered
																? "Already Registered"
																: loadingEvents[event._id]
																	? <Loader />
																	: "Register"
															}
														</button>
													)}
											</div>
										</div>
									</div>
								);
							})
							: <p className="no-event">No events found</p>
						}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Event;
